import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, hasRole, getUser } from '../utils/auth';
import type { PermissionResource, PermissionAction } from '../types';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  requiredPermission?: {
    resource: PermissionResource;
    action: PermissionAction;
  };
}

/**
 * ProtectedRoute component that handles authentication and authorization
 *
 * Usage:
 * ```tsx
 * // Basic authentication only
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * // With role requirement
 * <ProtectedRoute requiredRoles={['store_manager', 'store_owner']}>
 *   <AdminPage />
 * </ProtectedRoute>
 *
 * // With permission requirement
 * <ProtectedRoute requiredPermission={{ resource: 'products', action: 'create' }}>
 *   <ProductCreate />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute = ({
  children,
  requiredRoles,
  requiredPermission,
}: ProtectedRouteProps) => {
  const location = useLocation();

  // Check authentication
  if (!isAuthenticated()) {
    // Redirect to login with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirement
  if (requiredRoles && !hasRole(requiredRoles)) {
    return <Navigate to="/app" replace />;
  }

  // Check permission requirement
  if (requiredPermission) {
    const user = getUser();

    // Admin bypass
    if (user?.isAdmin) {
      return <>{children}</>;
    }

    // Check if user has the required permission
    const permissionKey = `${requiredPermission.resource}:${requiredPermission.action}`;
    const hasPermission = user?.permissions?.includes(permissionKey) ?? false;

    if (!hasPermission) {
      // Redirect to dashboard if no permission
      return <Navigate to="/app" replace />;
    }
  }

  return <>{children}</>;
};

/**
 * Higher-order component to wrap a page with permission requirements
 */
export const withPermission = (
  WrappedComponent: React.ComponentType,
  resource: PermissionResource,
  action: PermissionAction
) => {
  return function PermissionWrappedComponent(props: any) {
    return (
      <ProtectedRoute requiredPermission={{ resource, action }}>
        <WrappedComponent {...props} />
      </ProtectedRoute>
    );
  };
};
